import React, { useState } from 'react';
import { Check, X, Edit2, Trash2, Plus } from 'lucide-react';
import { useRecipeStore } from '../store/recipeStore';
import { GrocerySearch } from './GrocerySearch';
import type { Meal, Recipe } from '../types/recipe';

export function MealBuilder() {
  const { recipes, meals, addMeal, updateMeal, deleteMeal } = useRecipeStore();
  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([]);
  const [mealName, setMealName] = useState('');
  const [mealNotes, setMealNotes] = useState('');
  const [editingMeal, setEditingMeal] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Meal> | null>(null);

  const handleCreateMeal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mealName || selectedRecipes.length === 0) return;

    await addMeal({
      name: mealName,
      recipeIds: selectedRecipes,
      notes: mealNotes
    });

    setMealName('');
    setMealNotes('');
    setSelectedRecipes([]);
  };

  const handleEdit = (meal: Meal) => {
    setEditingMeal(meal.id);
    setEditForm(meal);
  };

  const handleCancelEdit = () => {
    setEditingMeal(null);
    setEditForm(null);
  };

  const handleSave = async () => {
    if (!editForm || !editingMeal) return;
    
    await updateMeal({
      ...editForm,
      id: editingMeal,
      recipeIds: editForm.recipeIds || []
    } as Meal);
    
    setEditingMeal(null);
    setEditForm(null);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this meal? This action cannot be undone.')) {
      await deleteMeal(id);
    }
  };

  const getMealIngredients = (recipeIds: string[]) => {
    return recipes
      .filter(recipe => recipeIds.includes(recipe.id))
      .flatMap(recipe => recipe.ingredients);
  };

  const toggleRecipeSelection = (recipeId: string) => {
    setSelectedRecipes(current =>
      current.includes(recipeId)
        ? current.filter(id => id !== recipeId)
        : [...current, recipeId]
    );
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleCreateMeal} className="space-y-4">
        <div>
          <label htmlFor="meal-name" className="block text-sm font-medium text-gray-700 mb-1">
            Meal Name
          </label>
          <input
            id="meal-name"
            type="text"
            value={mealName}
            onChange={(e) => setMealName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter meal name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Recipes
          </label>
          <div className="space-y-2">
            {recipes.map((recipe) => (
              <label
                key={recipe.id}
                className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selectedRecipes.includes(recipe.id)}
                  onChange={() => toggleRecipeSelection(recipe.id)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>{recipe.title}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="meal-notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="meal-notes"
            value={mealNotes}
            onChange={(e) => setMealNotes(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={4}
            placeholder="Add any notes about this meal..."
          />
        </div>

        <button
          type="submit"
          disabled={!mealName || selectedRecipes.length === 0}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <Plus size={20} />
          Create Meal
        </button>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {recipes.map((recipe) => (
          <label
            key={recipe.id}
            className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50"
          >
            <input
              type="checkbox"
              checked={selectedRecipes.includes(recipe.id)}
              onChange={() => toggleRecipeSelection(recipe.id)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>{recipe.title}</span>
          </label>
        ))}
      </div>

      <div className="space-y-4">
        {meals.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No meals yet. Create your first meal by selecting recipes above!
          </div>
        ) : (
          meals.map((meal) => (
            <div key={meal.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                {editingMeal === meal.id ? (
                  <div className="flex-1">
                    <input
                      type="text"
                      value={editForm?.name || ''}
                      onChange={(e) => setEditForm({ ...editForm!, name: e.target.value })}
                      className="w-full px-2 py-1 border rounded"
                    />
                  </div>
                ) : (
                  <h4 className="text-lg font-medium">{meal.name}</h4>
                )}
                <div className="flex items-center gap-2">
                  {editingMeal === meal.id ? (
                    <>
                      <button
                        onClick={handleSave}
                        className="p-1 text-green-600 hover:text-green-700"
                        title="Save changes"
                      >
                        <Check size={20} />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1 text-gray-600 hover:text-gray-700"
                        title="Cancel editing"
                      >
                        <X size={20} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEdit(meal)}
                        className="p-1 text-gray-600 hover:text-gray-700"
                        title="Edit meal"
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() => handleDelete(meal.id)}
                        className="p-1 text-red-600 hover:text-red-700"
                        title="Delete meal"
                      >
                        <Trash2 size={20} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium mb-2">Recipes</h5>
                  <div className="space-y-1">
                    {recipes
                      .filter((recipe) => meal.recipeIds.includes(recipe.id))
                      .map((recipe) => (
                        <div key={recipe.id} className="text-gray-600">
                          • {recipe.title}
                        </div>
                      ))}
                  </div>
                </div>
                <div>
                  <h5 className="font-medium mb-2">Notes</h5>
                  {editingMeal === meal.id ? (
                    <textarea
                      value={editForm?.notes || ''}
                      onChange={(e) => setEditForm({ ...editForm!, notes: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={4}
                      placeholder="Add any notes about this meal..."
                    />
                  ) : meal.notes ? (
                    <div className="text-gray-600 whitespace-pre-wrap">{meal.notes}</div>
                  ) : (
                    <div className="text-gray-400 italic">No notes added</div>
                  )}
                </div>
                <GrocerySearch ingredients={getMealIngredients(meal.recipeIds)} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}